import axios from "axios";
import { useEffect, useState } from "react";
import { useDataContext } from "../../../utils/DataContext";
import { buttonText } from "../../../utils/buttonText";
import { pageText } from "../../../utils/pageText";

const defaultSettings = {
  external_mode: true,
  llm_base_url: "",
  llm_api_key: "",
  llm_model_name: "",
  llm_think_mode: false,
  image_edit_base_url: "",
  image_edit_api_key: "",
  image_edit_model: "",
  search_base_url: "",
  search_api_key: "",
};

const toggleFields = [
  ["llm_think_mode", "LLM think mode", "Enable extended reasoning before responses"],
];

const llmProviders = [
  {
    value: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    label: "Alibaba Cloud Bailian",
    models: [
      { value: "qwen3.7-max", label: "qwen3.7-max" },
      { value: "qwen3.7-plus", label: "qwen3.7-plus" },
      { value: "qwen3.6-plus", label: "qwen3.6-plus" },
    ],
  },
  {
    value:
      "https://generativelanguage.googleapis.com/v1beta/openai/",
    label: "Google Gemini",
    models: [
      { value: "gemini-3.1-flash-lite", label: "gemini-3.1-flash-lite" },
      { value: "gemini-3.5-flash-lit", label: "gemini-3.5-flash-lit" },
      { value: "gemini-3.6-flash", label: "gemini-3.6-flash" },
    ],
  },
  {
    value: "https://api.openai.com/v1",
    label: "OpenAI",
    models: [
      { value: "gpt-4o", label: "gpt-4o" },
      { value: "GPT-5.6", label: "GPT-5.6" },
    ],
  },
];

const imageEditProviders = [
  {
    value: "https://dashscope.aliyuncs.com/api/v1",
    label: "Alibaba Cloud Bailian",
    models: [
      { value: "qwen-image-2.0", label: "qwen-image-2.0" },
      { value: "qwen-image-edit", label: "qwen-image-edit" },
    ],
  },
  {
    value: "https://generativelanguage.googleapis.com/v1beta/openai/",
    label: "Google Gemini",
    models: [
      { value: "gemini-3.1-flash-image", label: "gemini-3.1-flash-image" },
      { value: "gemini-3.1-flash-lite-image", label: "gemini-3.1-flash-lite-image" },
    ],
  },
  {
    value: "https://api.stepfun.com/v1",
    label: "StepFun",
    models: [{ value: "step-image-edit-2", label: "step-image-edit-2" }],
  },
];

const searchProviders = [
  { value: "https://api.exa.ai/search", label: "EXA", models: [] },
];

const providerConfig = {
  llm_base_url: { providers: llmProviders, modelKey: "llm_model_name" },
  image_edit_base_url: {
    providers: imageEditProviders,
    modelKey: "image_edit_model",
  },
  search_base_url: { providers: searchProviders, modelKey: null },
};

const modelFieldToBaseUrlKey = {
  llm_model_name: "llm_base_url",
  image_edit_model: "image_edit_base_url",
};

const getProvider = (providers, baseUrl) =>
  providers.find((p) => p.value === baseUrl);

const getModelsForBaseUrl = (providers, baseUrl) =>
  getProvider(providers, baseUrl)?.models || [];

// section title -> rows; each row is an array of fields [key, label, placeholder]
const sections = [
  {
    title: "Language model",
    rows: [
      [["llm_base_url", "Base URL", "https://api.openai.com/v1"]],
      [
        ["llm_api_key", "API key", ""],
        ["llm_model_name", "Model name", "gpt-4o"],
      ],
    ],
  },
  {
    title: "Image editing",
    rows: [
      [["image_edit_base_url", "Base URL", "https://api.provider.com/v1"]],
      [
        ["image_edit_api_key", "API key", ""],
        ["image_edit_model", "Model", "dall-e-3"],
      ],
    ],
  },
  {
    title: "Search",
    rows: [
      [
        ["search_base_url", "Base URL", "https://api.search.com"],
        ["search_api_key", "API key", ""],
      ],
    ],
  },
];

function OrganizationApiKeysPage({ organizationName }) {
  const { userToken } = useDataContext();
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSettings = async () => {
    if (!organizationName || !userToken) return;
    try {
      const response = await axios.get("/auth/organization/api-keys", {
        params: {
          requesterToken: userToken,
          organization: organizationName,
        },
      });
      setSettings({ ...defaultSettings, ...response.data });
      setDirty(false);
    } catch (error) {
      window.alert(error.response?.data || error.message);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [organizationName, userToken]);

  const updateField = (key, value) => {
    const next = { ...settings, [key]: value };

    const baseUrlConfig = providerConfig[key];
    if (baseUrlConfig?.modelKey) {
      const validModels = getModelsForBaseUrl(
        baseUrlConfig.providers,
        value
      ).map((m) => m.value);
      if (
        next[baseUrlConfig.modelKey] &&
        !validModels.includes(next[baseUrlConfig.modelKey])
      ) {
        next[baseUrlConfig.modelKey] = "";
      }
    }

    setSettings(next);
    setDirty(true);
  };

  const getSelectOptions = (key) => {
    if (providerConfig[key]) {
      return providerConfig[key].providers.map(({ value, label }) => ({
        value,
        label,
      }));
    }

    const baseUrlKey = modelFieldToBaseUrlKey[key];
    if (baseUrlKey) {
      const baseUrl = settings[baseUrlKey];
      const providers = providerConfig[baseUrlKey]?.providers || [];
      return getModelsForBaseUrl(providers, baseUrl);
    }

    return null;
  };

  const persistSettings = async (nextSettings, successMessage) => {
    setLoading(true);
    try {
      await axios.post("/auth/organization/api-keys/set", {
        requesterToken: userToken,
        organization: organizationName,
        settings: nextSettings,
      });
      setDirty(false);
      setSettings(nextSettings);
      window.alert(successMessage);
    } catch (error) {
      window.alert(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    persistSettings(
      settings,
      `Update api keys for '${organizationName}' success.`
    );
  };

  const deleteSettings = async () => {
    setLoading(true);
    try {
      await axios.post("/auth/organization/api-keys/delete", {
        requesterToken: userToken,
        organization: organizationName,
      });
      setSettings(defaultSettings);
      setDirty(false);
      window.alert("API key settings deleted.");
    } catch (error) {
      window.alert(error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderField = ([key, label, placeholder]) => {
    const options = getSelectOptions(key);
    const value = settings[key] || "";
    const baseUrlKey = modelFieldToBaseUrlKey[key];
    const modelDisabled = baseUrlKey && !settings[baseUrlKey];

    if (options) {
      const knownValues = options.map((o) => o.value);
      const hasUnknownValue = value && !knownValues.includes(value);

      return (
        <div className="org-field" key={key}>
          <label className="org-field-label" htmlFor={`apikey-${key}`}>
            {pageText(label)}
          </label>
          <select
            id={`apikey-${key}`}
            className="org-field-input"
            value={value}
            disabled={modelDisabled}
            onChange={(e) => updateField(key, e.target.value)}
          >
            {!value && (
              <option value="" disabled>
                {modelDisabled
                  ? pageText("Select base URL first")
                  : pageText(`Select ${label.toLowerCase()}`)}
              </option>
            )}
            {hasUnknownValue && <option value={value}>{value}</option>}
            {options.map(({ value: optValue, label: optLabel }) => (
              <option key={optValue} value={optValue}>
                {optLabel}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="org-field" key={key}>
        <label className="org-field-label" htmlFor={`apikey-${key}`}>
          {pageText(label)}
        </label>
        <input
          id={`apikey-${key}`}
          className="org-field-input"
          type={key.endsWith("api_key") ? "password" : "text"}
          value={value}
          placeholder={pageText(placeholder)}
          onChange={(e) => updateField(key, e.target.value)}
        />
      </div>
    );
  };

  return (
    <div className="org-api-keys-layout">
      <div className="org-card">
        <div className="org-card-header">
          <div>
            <h2 className="org-card-title">{pageText("API keys")}</h2>
            <p className="org-card-subtitle">
              {pageText("Configure model, image, and search providers for this workspace")}
            </p>
          </div>
          {!dirty && <span className="org-saved-badge">{pageText("Saved")}</span>}
        </div>

        <div className="org-card-body">
          <div className="org-api-keys-form">
            <div className="org-section-eyebrow">{pageText("Mode")}</div>
            {toggleFields.map(([key, label, desc]) => (
              <div className="org-toggle-row" key={key}>
                <div>
                  <p className="org-toggle-title">{pageText(label)}</p>
                  <p className="org-toggle-desc">{pageText(desc)}</p>
                </div>
                <button
                  type="button"
                  className={`org-switch ${settings[key] ? "on" : ""}`}
                  role="switch"
                  aria-checked={settings[key]}
                  onClick={() => updateField(key, !settings[key])}
                >
                  <span className="org-switch-knob" />
                </button>
              </div>
            ))}

            {sections.map((section) => (
              <div className="org-section" key={section.title}>
                <div className="org-section-eyebrow">{pageText(section.title)}</div>
                {section.rows.map((row, idx) => (
                  <div
                    className={`org-field-row ${row.length > 1 ? "two-col" : ""}`}
                    key={idx}
                  >
                    {row.map(renderField)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="org-card-footer">
          <span className="org-footer-note">
            <span className="org-lock-icon" />
            {pageText("Keys are encrypted at rest")}
          </span>
          <div className="org-footer-actions">
            <button
              type="button"
              className="org-btn org-btn-danger"
              onClick={deleteSettings}
              disabled={loading}
            >
              {buttonText("Delete settings")}
            </button>
            <button
              type="button"
              className="org-btn org-btn-secondary"
              onClick={fetchSettings}
              disabled={loading}
            >
              {buttonText("Cancel")}
            </button>
            <button
              type="button"
              className="org-btn org-btn-primary"
              onClick={saveSettings}
              disabled={loading}
            >
              {buttonText(loading ? "Saving..." : "Save API keys")}
            </button>
          </div>
        </div>
      </div>

      <aside className="org-api-keys-guide">
        <div className="org-section-eyebrow">{pageText("Recommended providers")}</div>
        <div className="org-guide-section">
          <h3>{pageText("Vision-capable LLMs")}</h3>
          <ol>
            <li>
              <strong>{pageText("Best cost / speed:")}</strong> Gemini{" "}
              <code>gemini-3.1-flash-lite</code> {pageText("or Bailian")}{" "}
              <code>qwen3.6-plus</code>{" "}
              {pageText("for lightweight anomaly synthesis and fast vision reasoning.")}
            </li>
            <li>
              <strong>{pageText("Balanced default:")}</strong> Bailian{" "}
              <code>qwen3.7-plus</code>{" "}
              {pageText("for routine anomaly synthesis with a good balance of quality, latency, and cost.")}
            </li>
            <li>
              <strong>{pageText("Higher quality:")}</strong> Bailian{" "}
              <code>qwen3.7-max</code> {pageText("or Gemini")} <code>gemini-3.6-flash</code>{" "}
              {pageText("for difficult defects and stronger visual reasoning.")}
            </li>
            <li>
              <strong>OpenAI:</strong> <code>gpt-4o</code> {pageText("or")}{" "}
              <code>GPT-5.6</code>{" "}{pageText("when your deployment already uses OpenAI.")}
            </li>
          </ol>
          <p>
            {pageText("Start with qwen3.7-plus; move to a faster model for high-volume workloads or a higher-quality model for difficult cases.")}
          </p>
        </div>
        <div className="org-guide-section">
          <h3>{pageText("Image generation / editing")}</h3>
          <ol>
            <li>
              <strong>{pageText("Best cost / speed:")}</strong> Gemini{" "}
              <code>gemini-3.1-flash-lite-image</code>{" "}
              {pageText("for quick drafts and lightweight edits.")}
            </li>
            <li>
              <strong>{pageText("Balanced default:")}</strong> Bailian{" "}
              <code>qwen-image-2.0</code>{" "}
              {pageText("for text-to-image, editing, and batch generation.")}
            </li>
            <li>
              <strong>{pageText("Higher quality:")}</strong> Gemini{" "}
              <code>gemini-3.1-flash-image</code>{" "}
              {pageText("when you need stronger consistency and visual fidelity.")}
            </li>
            <li>
              <strong>{pageText("Edit-focused:")}</strong> Bailian{" "}
              <code>qwen-image-edit</code> {pageText("for image editing, or StepFun")}{" "}
              <code>step-image-edit-2</code>{" "}
              {pageText("as an OpenAI-compatible alternative.")}
            </li>
          </ol>
          <p>
            {pageText("Use Gemini lite for speed, qwen-image-2.0 for general workloads, and Gemini flash-image when output quality is the priority.")}
          </p>
        </div>
        <div className="org-guide-section">
          <h3>{pageText("Search")}</h3>
          <p>
            {pageText("Prefer")} <strong>EXA</strong> {pageText("for search in MLOps")} -{" "}
            {pageText("predictable HTTP API and good relevance/cost trade-off. Use alternatives only if you already have another compatible search endpoint.")}
          </p>
        </div>
      </aside>
    </div>
  );
}

export default OrganizationApiKeysPage;
